import * as Immutable from "immutable";
export default class User {
  constructor(id, username, fullName, firstName, lastName, email, permissions, timezone, preferences, roles, readOnly, external, sessionTimeoutMs, startpage, sessionActive, clientAddress, lastActivity, accountStatus, serviceAccount, grnPermissions, authServiceEnabled) {
    this._value = {
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      permissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      grnPermissions,
      serviceAccount,
      authServiceEnabled
    };
  }
  get id() {
    return this._value.id;
  }
  get username() {
    return this._value.username;
  }
  get fullName() {
    return this._value.fullName;
  }
  get firstName() {
    return this._value.firstName;
  }
  get lastName() {
    return this._value.lastName;
  }
  get email() {
    return this._value.email;
  }
  get grnPermissions() {
    return this._value.grnPermissions;
  }
  get permissions() {
    return this._value.permissions;
  }
  get timezone() {
    return this._value.timezone;
  }
  get preferences() {
    return this._value.preferences;
  }
  get roles() {
    return this._value.roles;
  }
  get readOnly() {
    return this._value.readOnly;
  }
  get external() {
    return this._value.external;
  }
  get accountStatus() {
    return this._value.accountStatus;
  }
  get serviceAccount() {
    return this._value.serviceAccount;
  }
  get sessionTimeoutMs() {
    return this._value.sessionTimeoutMs;
  }
  get sessionTimeout() {
    if (!this.sessionTimeoutMs) {
      return void 0;
    }
    const MS_DAY = 24 * 60 * 60 * 1e3;
    const MS_HOUR = 60 * 60 * 1e3;
    const MS_MINUTE = 60 * 1e3;
    const MS_SECOND = 1e3;
    const _estimateUnit = (value2) => {
      if (value2 === 0) {
        return [MS_SECOND, "Seconds"];
      }
      if (value2 % MS_DAY === 0) {
        return [MS_DAY, "Days"];
      }
      if (value2 % MS_HOUR === 0) {
        return [MS_HOUR, "Hours"];
      }
      if (value2 % MS_MINUTE === 0) {
        return [MS_MINUTE, "Minutes"];
      }
      return [MS_SECOND, "Seconds"];
    };
    const unit = _estimateUnit(this.sessionTimeoutMs);
    const value = Math.floor(this.sessionTimeoutMs / unit[0]);
    return {
      value,
      unitMS: unit[0],
      unitString: unit[1]
    };
  }
  get startpage() {
    return this._value.startpage;
  }
  get sessionActive() {
    return this._value.sessionActive;
  }
  get clientAddress() {
    return this._value.clientAddress;
  }
  get lastActivity() {
    return this._value.lastActivity;
  }
  get authServiceEnabled() {
    return this._value.authServiceEnabled;
  }
  toBuilder() {
    const {
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      permissions,
      grnPermissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      serviceAccount,
      authServiceEnabled
    } = this._value;
    return new Builder(
      Immutable.Map({
        id,
        username,
        fullName,
        firstName,
        lastName,
        email,
        permissions,
        timezone,
        grnPermissions,
        preferences,
        roles,
        readOnly,
        external,
        sessionTimeoutMs,
        startpage,
        sessionActive,
        clientAddress,
        lastActivity,
        accountStatus,
        serviceAccount,
        authServiceEnabled
      })
    );
  }
  static create(id, username, fullName, firstName, lastName, email, permissions, timezone, preferences, roles, readOnly, external, sessionTimeoutMs, startpage, sessionActive, clientAddress, lastActivity, accountStatus, serviceAccount, grnPermissions, authServiceEnabled) {
    return new User(
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      permissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      serviceAccount,
      grnPermissions,
      authServiceEnabled
    );
  }
  static empty() {
    return User.create(
      "",
      "",
      "",
      "",
      Immutable.List(),
      "",
      {},
      Immutable.Set(),
      false,
      false,
      -1,
      void 0,
      false,
      "",
      "",
      "enabled"
    );
  }
  toJSON() {
    const {
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      grnPermissions,
      permissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      serviceAccount,
      authServiceEnabled
    } = this._value;
    return {
      id,
      username,
      full_name: fullName,
      first_name: firstName,
      last_name: lastName,
      email,
      grn_permissions: grnPermissions ? grnPermissions.toJS() : [],
      permissions: permissions ? permissions.toArray() : [],
      timezone,
      preferences,
      roles: roles ? roles.toArray() : [],
      read_only: readOnly,
      external,
      session_timeout_ms: sessionTimeoutMs,
      startpage,
      session_active: sessionActive,
      client_address: clientAddress,
      last_activity: lastActivity,
      account_status: accountStatus,
      service_account: serviceAccount,
      auth_service_enabled: authServiceEnabled
    };
  }
  static fromJSON(value) {
    const {
      id,
      username,
      full_name,
      first_name,
      last_name,
      email,
      grn_permissions,
      permissions,
      timezone,
      preferences,
      roles,
      read_only,
      external,
      session_timeout_ms,
      startpage,
      session_active,
      client_address,
      last_activity,
      account_status,
      service_account,
      auth_service_enabled
    } = value;
    return User.create(
      id,
      username,
      full_name,
      first_name,
      last_name,
      email,
      Immutable.List(permissions),
      timezone,
      preferences,
      Immutable.Set(roles),
      read_only,
      external,
      session_timeout_ms,
      startpage,
      session_active,
      client_address,
      last_activity,
      account_status,
      service_account,
      Immutable.List(grn_permissions),
      auth_service_enabled
    );
  }
  static builder() {
    return new Builder();
  }
}
class Builder {
  constructor(value = Immutable.Map()) {
    this.value = value;
  }
  id(value) {
    return new Builder(this.value.set("id", value));
  }
  username(value) {
    return new Builder(this.value.set("username", value));
  }
  fullName(value) {
    return new Builder(this.value.set("fullName", value));
  }
  firstName(value) {
    return new Builder(this.value.set("firstName", value));
  }
  lastName(value) {
    return new Builder(this.value.set("lastName", value));
  }
  email(value) {
    return new Builder(this.value.set("email", value));
  }
  grnPermissions(value) {
    return new Builder(this.value.set("grnPermissions", value));
  }
  permissions(value) {
    return new Builder(this.value.set("permissions", Immutable.List(value)));
  }
  timezone(value) {
    return new Builder(this.value.set("timezone", value));
  }
  preferences(value) {
    return new Builder(this.value.set("preferences", value));
  }
  roles(value) {
    return new Builder(this.value.set("roles", value));
  }
  readOnly(value) {
    return new Builder(this.value.set("readOnly", value));
  }
  external(value) {
    return new Builder(this.value.set("external", value));
  }
  sessionTimeoutMs(value) {
    return new Builder(this.value.set("sessionTimeoutMs", value));
  }
  startpage(value) {
    return new Builder(this.value.set("startpage", value));
  }
  sessionActive(value) {
    return new Builder(this.value.set("sessionActive", value));
  }
  clientAddress(value) {
    return new Builder(this.value.set("clientAddress", value));
  }
  lastActivity(value) {
    return new Builder(this.value.set("lastActivity", value));
  }
  accountStatus(value) {
    return new Builder(this.value.set("accountStatus", value));
  }
  serviceAccount(value) {
    return new Builder(this.value.set("serviceAccount", value));
  }
  authServiceEnabled(value) {
    return new Builder(this.value.set("authServiceEnabled", value));
  }
  build() {
    const {
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      permissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      serviceAccount,
      grnPermissions,
      authServiceEnabled
    } = this.value.toObject();
    return new User(
      id,
      username,
      fullName,
      firstName,
      lastName,
      email,
      permissions,
      timezone,
      preferences,
      roles,
      readOnly,
      external,
      sessionTimeoutMs,
      startpage,
      sessionActive,
      clientAddress,
      lastActivity,
      accountStatus,
      serviceAccount,
      grnPermissions,
      authServiceEnabled
    );
  }
}
